import React from 'react'
import uuidv4 from 'uuid/v4'
import { debounce, cloneDeep } from 'lodash'
import { Card, CardActions, CardText } from 'material-ui-legacy/Card'
import { grey50 } from 'material-ui-legacy/styles/colors'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'
import DatePicker from 'material-ui-legacy/DatePicker'
import TextField from 'material-ui-legacy/TextField'

import { formatDate, dateToUTC } from '../../lib/formats'
import errors from '../../lib/errors.js'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'
import LinearLoader from '../UI/LinearLoader'
import {
  ProjectSummary,
  GrossTotal,
  GrandTotal,
  TaxInput
} from './QuoteFormComponents'
import CategoryActualLineItemsContainer from './CategoryActualLineItemsContainer'

import Resource from '../../stores/resource'
const ActualLineItemStore = Resource('actual-line-items')
const CategoryStore = Resource('service-categories')

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- actual: a complete actual object
  or
- Actual fields: will be inserted in the form when present
- projectTypeId: self-explanatory
- submitCallback: function(attributes)
  callback function called on form submission. This is *only*
  called when the form is valid and the contents have changed. Receives actual
  attributes.
- cancelCallback: function(), callback function for the cancel button.
- changedCallback: function(), callback function called when the form contents have changed.
  Receives true if the contents have changed.
- forceChanged: Boolean, force the form status to "changed" if true
*/
class ActualForm extends React.Component {
  constructor(props) {
    super(props)
    // We have to build the state on a regular variable so
    // React doesn't complain about direct state mutation.
    let initialState = {
      formValid: true,
      formChanged: this.props.forceChanged || false,
      formSubmitted: false,
      actualId: this.props.actual ? this.props.actual.id : null,
      actualLineItems: [],
      categories: [],
      taxTotal: 0,
      loading: true
    }

    // We can either receive a single data prop or each field separately
    this.initialData = this.props.actual
      ? this.props.actual.attributes
      : this.props

    this.fields = [
      'projectId',
      'name',
      'date',
      'version',
      'description',
      'jobDescription',
      'contacts',
      'taxRate',
      'taxCode',
      'notes'
    ]
    
   for (let field of this.fields) {
      if (field === 'taxRate') {
        if (this.props.projectTypeName === 'Audio Production US') {
          initialState[ field ] = {
            value: this.initialData[ field ] ? this.initialData[ field ] : '0',
            valid: true
          }
        } else {
          initialState[ field ] = {
            value: this.initialData[field] || this.initialData[field] === 0 ? `${this.initialData[field]}` : '0.13', //default HST and 0.13 for everything apart from Audio Production US
            valid: true
          }
        }
      } else {
        initialState[field] = {
          value: this.initialData[field] || '',
          valid: true
        }

      }
    }

    //Default value for Audio Production US
    if (this.props.projectTypeName === 'Audio Production US') {
      initialState.taxCode.value = initialState.taxCode.value || 'Base Tax'
    } else {
      // Default values
      initialState.taxCode.value = initialState.taxCode.value || 'HST'
    }

    // Et voilà!
    this.state = initialState

    // Debouncing change and validation hooks for better performance
    this._checkForChanges = debounce(this._checkForChanges, 100)
    this._handleBatchActualLineItems = debounce(
      this._handleBatchActualLineItems,
      100
    )
  }

  componentDidMount() {
    const { actualId } = this.state
    const { projectTypeId, forceChanged } = this.props
    const categoryFilter = projectTypeId ? { projectTypeId: projectTypeId } : {}

    const requests = [
      CategoryStore.findAll(categoryFilter),
      ActualLineItemStore.findAll({ actualId: actualId }, ['service'])
    ]

    Promise.all(requests)
      .then(results => {
        const [categoryResult, actualLineItemResult] = results
        let actualLineItems = actualLineItemResult.data
        actualLineItems = actualLineItems.map(li => {
          li.key = uuidv4()
          li.service = li.included('service')
          return li
        })
        // make a PROTECTED copy of actualLineItems outside of the state
        this.originalLineItems = cloneDeep(actualLineItems)

        // Set state
        const totals = this._calculateTotals(actualLineItems)
        this.setState({
          categories: categoryResult.data,
          actualLineItems: actualLineItems,
          totalCost: totals.totalCost,
          grossTotal: totals.grossTotal,
          taxTotal: totals.taxTotal,
          grandTotal: totals.grandTotal,
          totalMargin: totals.totalMargin,
          totalIsExact: totals.totalIsExact,
          loading: false
        })
      })
      .catch(error =>
        this.setState({ error: errors.unpack(error), loading: false })
      )

    if (forceChanged) this._validateForm()
  }

  _submit = () => {
    const { submitCallback } = this.props
    const {
      formValid,
      formChanged,
      actualLineItems
    } = this.state

    // Don't do anything if we have invalid fields or no changes.
    if (!formValid || !formChanged) return

    let attributes = {}
    for (let field of this.fields) {
      attributes[field] = this.state[field].value
    }
    attributes.actualLineItems = actualLineItems

    if (submitCallback) submitCallback(attributes)
    this.setState({ formChanged: false, formSubmitted: true })
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = { value: value, valid: valid }
    if (attribute === 'taxRate') this._updateActualLineItems()
    this.setState(updates, () => {
      this._checkForChanges()
    })
  }

  _validateForm = () => {
    let allValid = true
    for (let field of this.fields) {
      if (!this.state[field].valid) {
        allValid = false
        break
      }
    }
    this.setState({ formValid: allValid })
  }

  _checkForChanges = () => {
    let changed = this.props.forceChanged || false
    for (let field of this.fields) {
      let current = this.state[field].value
      let original = this.initialData[field] || ''
      if (current !== original) {
        changed = true
        break
      }
    }
    this.setState({ formChanged: changed }, () => {
      if (this.props.changedCallback) {
        this.props.changedCallback(this.state.formChanged)
      }
    })
  }

  // Receives a batch of actualLineItem updates from CategoryActualLineItemsContainer,
  // consolidates all changes and calculate grand totals
  _handleBatchActualLineItems = (changedActualLineItems, categoryId) => {
    // Clone actualLineItems and delete the ones matching categoryId
    let newActualLineItems = this.state.actualLineItems.filter(
      actualLineItem =>
        actualLineItem.service.attributes &&
        Number(actualLineItem.service.attributes.serviceCategoryId) !==
          Number(categoryId)
    )
    // Concatenate new actualLineItems and update state
    this._updateActualLineItems(
      newActualLineItems.concat(changedActualLineItems)
    )
  }

  // Updates actualLineItems and totals at the same time
  _updateActualLineItems = (actualLineItems = this.state.actualLineItems) => {
    const { changedCallback } = this.props
    const { formChanged } = this.state
    const totals = this._calculateTotals(actualLineItems)

    this.setState(
      {
        actualLineItems: actualLineItems,
        totalCost: totals.totalCost,
        grossTotal: totals.grossTotal,
        taxTotal: totals.taxTotal,
        grandTotal: totals.grandTotal,
        totalMargin: totals.totalMargin,
        totalIsExact: totals.totalIsExact,
        formChanged: true
      },
      () => {
        if (changedCallback) changedCallback(formChanged)
        this.forceUpdate()
      }
    )
  }

  _calculateTotals = (actualLineItems = this.state.actualLineItems) => {
    let totalCost = 0.0
    let grossTotal = 0.0
    let totalIsExact = true
    for (let actualLineItem of actualLineItems) {
      let attributes = actualLineItem.attributes
      totalCost +=
        Number(attributes.actualCost) * Number(attributes.actualNumber)
      grossTotal += Number(attributes.actualTotal)
      if (totalIsExact) totalIsExact = Boolean(attributes.actualCost)
    }
    const totalMargin = // Avoid division by zero
      grossTotal > 0 ? (1 - totalCost / grossTotal) * 100 : 0
    const taxTotal =
      grossTotal > 0 ? grossTotal * Number(this.state.taxRate.value) : 0
    const grandTotal = grossTotal + taxTotal

    return {
      totalCost,
      grossTotal,
      taxTotal,
      grandTotal,
      totalMargin,
      totalIsExact
    }
  }

  render() {
    const {
      budget,
      estimatedCost,
      targetMargin,
      cancelCallback,
      submitCallback
    } = this.props

    const {
      actualId,
      name,
      date,
      jobDescription,
      notes,
      categories,
      actualLineItems,
      grossTotal,
      grandTotal,
      totalIsExact,
      totalMargin,
      totalCost,
      taxCode,
      taxRate,
      taxTotal,
      formChanged,
      formValid,
      formSubmitted,
      loading
    } = this.state

    const dateValue = date.value ? dateToUTC(date.value) : null
    const budgetSignal = grandTotal <= budget
    const targetMarginSignal = totalMargin >= targetMargin
    const estimatedCostSignal = totalCost <= estimatedCost

    return (
      <Card>
        <CardText style={{ backgroundColor: grey50 }}>
          <Row>
            <Col sm="12" md="7" lg="8">
              <Col sm="12" md="8">
                <ValidatedTextInput
                  name="name"
                  required
                  fullWidth
                  floatingLabelText="Actual version"
                  hintText="Actual version"
                  errorMessage="You must provide a version name"
                  value={name.value}
                  validatedOnChange={this._change}
                />
              </Col>
              <Col sm="12" md="4">
                <DatePicker
                  fullWidth
                  hintText="Actual date"
                  floatingLabelText="Actual date"
                  value={dateValue}
                  defaultDate={dateToUTC()}
                  formatDate={date => formatDate(date)}
                  onChange={(ev, value) => this._change(value, true, 'date')}
                />
              </Col>
            </Col>

            <Col sm="12" md="5" lg="4" style={{ textAlign: 'right' }}>
              <ProjectSummary
                budget={budget}
                budgetSignal={budgetSignal}
                estimatedCost={estimatedCost}
                estimatedCostSignal={estimatedCostSignal}
                targetMargin={targetMargin * 100}
                targetMarginSignal={targetMarginSignal}
              />
            </Col>

            <Col sm="12">
              <Col sm="12" md="6">
                <TextField
                  name="jobDescription"
                  floatingLabelText="Job description (visible to client, leave blank to use project's)"
                  multiLine
                  fullWidth
                  rows={1}
                  rowsMax={15}
                  value={jobDescription.value}
                  onChange={(ev, value) =>
                    this._change(value, true, 'jobDescription')
                  }
                />
              </Col>
              <Col sm="12" md="6">
                <TextField
                  name="notes"
                  floatingLabelText="Notes (visible to client)"
                  multiLine
                  fullWidth
                  rows={1}
                  rowsMax={15}
                  value={notes.value}
                  onChange={(ev, value) => this._change(value, true, 'notes')}
                />
              </Col>
            </Col>
          </Row>
        </CardText>

        <CardText>
          {loading && <LinearLoader />}
          {categories.map((category, index) => (
            <CategoryActualLineItemsContainer
              key={`cat-${index}`}
              actualId={actualId}
              category={category}
              actualLineItems={actualLineItems}
              originalLineItems={this.originalLineItems}
              batchOnChange={this._handleBatchActualLineItems}
            />
          ))}
        </CardText>

        <CardText style={{ backgroundColor: grey50 }}>
          <GrossTotal
            totalCost={totalCost}
            totalMargin={totalMargin}
            targetMargin={targetMargin}
            grossTotal={grossTotal}
            isExact={totalIsExact}
          />
          <TaxInput
            taxCode={taxCode.value}
            taxRate={taxRate.value}
            taxTotal={taxTotal}
            validatedOnChange={this._change}
          />
          <GrandTotal grandTotal={grandTotal} />
        </CardText>

        <CardActions style={{ textAlign: 'center', backgroundColor: grey50 }}>
          {submitCallback && (
            <SaveButton
              type="submit"
              disabled={!formChanged || !formValid}
              onTouchTap={this._submit}
            />
          )}
          {cancelCallback && (
            <CancelButton
              onTouchTap={cancelCallback}
              disabled={!formChanged && formSubmitted}
            />
          )}
        </CardActions>
      </Card>
    )
  }
}

export default ActualForm



// WEBPACK FOOTER //
// ./src/components/Quotes/ActualForm.jsx